"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import { supabase } from "../supabaseClient";
import type { Session } from "@supabase/supabase-js";

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export default function PublicRoute({
  children,
  redirectTo = "/onboarding-form",
}: PublicRouteProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Initial session fetch
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    // Keep UI in sync with auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
      setLoading(false);
    });

    // Handle background tab throttling
    const handleVisibility = async () => {
      if (document.visibilityState === "visible") {
        (supabase.auth as any).startAutoRefresh?.();
        try {
          const { data } = await supabase.auth.getSession();
          if (!mounted) return;
          setSession(data.session);
          setLoading(false);
        } catch {
          /* no-op */
        }
      } else {
        (supabase.auth as any).stopAutoRefresh?.();
      }
    };

    // Extra safety: on window focus, re-check the session
    const handleFocus = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        setLoading(false);
      } catch {
        /* no-op */
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    // Run once to align with current visibility state
    handleVisibility();

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  // If user is logged in, redirect to dashboard
  return session ? <Navigate to={redirectTo} replace /> : <>{children}</>;
}
